/**
 * Admin API Client for YourModel.eu
 * Handles admin operations: user management, credits, whitelist
 */

import { getAuthToken } from './auth';
import config from './config';

const API_BASE_URL = `${config.API_BASE_URL}/api`;

// ==================== TYPES ====================

export interface User {
  username: string;
  email: string;
  role: 'admin' | 'user' | 'b2b-user';
  credits: number | string;  // number or "unlimited"
  credits_used: number;
  status: 'active' | 'disabled';
  auth_method: 'local' | 'google';
  created_date: string;
  last_login: string | null;
}

export interface CreateUserData {
  username: string;
  email: string;
  password: string;
  role: 'admin' | 'user' | 'b2b-user' | 'mobile-users';
  initial_credits?: number;
}

export interface UpdateUserData {
  email?: string;
  role?: 'admin' | 'user' | 'b2b-user' | 'mobile-users';
  status?: 'active' | 'disabled';
}

export interface CreditsHistoryEntry {
  timestamp: string;
  operation: string;
  cost: number;
  credits_before: number;
  credits_after: number;
  details?: Record<string, unknown>;
}

export interface UsageRecord {
  username: string;
  generation_id: string;
  timestamp: string;
  operation_type: string;
  status: 'success' | 'failed' | string;
  model_name?: string;
  api_provider?: string;
  cost_credits?: number;
  image_count?: number;
  generation_time_s?: number;
  error_message?: string;
  s3_metadata_key?: string;
}

export interface UsageTimelinePoint {
  date: string;
  generations: number;
  credits: number;
  failed_generations: number;
  failed_ratio: number;
  avg_generation_time_s: number;
}

export type UsageGroupByRow<T extends string> = {
  generations: number;
  credits: number;
  failed_generations: number;
  failed_ratio: number;
  avg_generation_time_s: number;
} & Record<T, string>;

export interface UsageStatsData {
  total_generations: number;
  total_credits: number;
  failed_generations: number;
  failed_ratio: number;
  avg_generation_time_s: number;
  by_user: Array<UsageGroupByRow<'username'>>;
  by_model: Array<UsageGroupByRow<'model_name'>>;
  by_operation: Array<UsageGroupByRow<'operation_type'>>;
  by_provider: Array<UsageGroupByRow<'api_provider'>>;
  timeline: UsageTimelinePoint[];
}

export interface UsageStatsResponse {
  success: boolean;
  stats: UsageStatsData;
  records: UsageRecord[];
  available_filters: {
    users: string[];
    models: string[];
    operations: string[];
    providers: string[];
    statuses: string[];
  };
  filters: Record<string, unknown>;
  meta: Record<string, unknown>;
  error?: string;
  message?: string;
}

export interface UsageStatsQuery {
  start_date: string;
  end_date: string;
  username?: string;
  operation_types?: string[];
  model_names?: string[];
  api_providers?: string[];
  statuses?: string[];
  records_limit?: number;
  max_records?: number;
}

export interface UsageMetadataResponse {
  success: boolean;
  username: string;
  generation_id: string;
  s3_key: string;
  metadata: any;
  error?: string;
  message?: string;
}

export type StylistaTaskStatus = 'queued' | 'processing' | 'completed' | 'failed';

export interface StylistaTask {
  task_id: string;
  username?: string;
  status?: StylistaTaskStatus | string;
  progress?: number | string;
  model?: string;
  credits_cost?: number;
  created_at?: string;
  updated_at?: string;
  started_at?: string;
  completed_at?: string;
  worker_id?: string;
  result_url?: string | null;
  error?: string | null;
}

export interface StylistaQueueResponse {
  success: boolean;
  tasks: StylistaTask[];
  total: number;
  pending_count: number;
  error?: string;
  message?: string;
}

export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  error?: string;
  data?: T;
}

// ==================== HELPER ====================

function getAuthHeaders(): HeadersInit {
  let token = getAuthToken();

  // Fallback: try to get user from localStorage and extract token
  if (!token) {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const userData = JSON.parse(userStr);
        token = userData.token;
      }
    } catch (e) {
      console.error('Failed to parse user data:', e);
    }
  }

  if (!token) {
    throw new Error('No authentication token found');
  }

  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
}

// ==================== USER MANAGEMENT ====================

/**
 * Get list of all users
 */
export async function listUsers(includeGoogle: boolean = true): Promise<User[]> {
  const url = `${API_BASE_URL}/admin/users?include_google=${includeGoogle}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to list users' }));
    throw new Error(error.message || error.error || 'Failed to list users');
  }

  const data = await response.json();
  return data.users || [];
}

/**
 * Get user details
 */
export async function getUser(username: string): Promise<User> {
  const response = await fetch(`${API_BASE_URL}/admin/users/${username}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to get user' }));
    throw new Error(error.message || error.error || 'Failed to get user');
  }

  const data = await response.json();
  return data.user;
}

/**
 * Create new user
 */
export async function createUser(userData: CreateUserData): Promise<ApiResponse> {
  const response = await fetch(`${API_BASE_URL}/admin/users`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(userData)
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || data.error || 'Failed to create user');
  }

  return data;
}

/**
 * Get Stylista queue tasks (admin only)
 */
export async function listStylistaTasks(params?: {
  limit?: number;
  status?: string;
  username?: string;
}): Promise<StylistaQueueResponse> {
  const query = new URLSearchParams();
  if (params?.limit) query.set('limit', String(params.limit));
  if (params?.status) query.set('status', params.status);
  if (params?.username) query.set('username', params.username);

  const url = `${API_BASE_URL}/admin/stylista/tasks${query.toString() ? `?${query}` : ''}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders()
  });

  const data = await response.json().catch(() => ({ message: 'Failed to list Stylista tasks' }));
  if (!response.ok) {
    throw new Error(data.message || data.error || 'Failed to list Stylista tasks');
  }

  return data as StylistaQueueResponse;
}

/**
 * Update user
 */
export async function updateUser(username: string, userData: UpdateUserData): Promise<ApiResponse> {
  const response = await fetch(`${API_BASE_URL}/admin/users/${username}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(userData)
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || data.error || 'Failed to update user');
  }

  return data;
}

/**
 * Delete user
 */
export async function deleteUser(username: string, hardDelete: boolean = false): Promise<ApiResponse> {
  const url = `${API_BASE_URL}/admin/users/${username}?hard=${hardDelete}`;
  const response = await fetch(url, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || data.error || 'Failed to delete user');
  }

  return data;
}

/**
 * Reset user password
 */
export async function resetPassword(username: string, newPassword: string): Promise<ApiResponse> {
  const response = await fetch(`${API_BASE_URL}/admin/users/${username}/password`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ new_password: newPassword })
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || data.error || 'Failed to reset password');
  }

  return data;
}

// ==================== CREDITS MANAGEMENT ====================

/**
 * Assign credits to user
 */
export async function assignCredits(username: string, credits: number): Promise<ApiResponse> {
  const response = await fetch(`${API_BASE_URL}/admin/credits/assign`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ username, credits })
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || data.error || 'Failed to assign credits');
  }

  return data;
}

/**
 * Get user's credits history
 */
export async function getCreditsHistory(username: string, limit: number = 50): Promise<CreditsHistoryEntry[]> {
  const response = await fetch(`${API_BASE_URL}/admin/credits/history/${username}?limit=${limit}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to get history' }));
    throw new Error(error.message || error.error || 'Failed to get history');
  }

  const data = await response.json();
  return data.history || [];
}

// ==================== USAGE TRACKING ====================

export async function getUsageStats(query: UsageStatsQuery): Promise<UsageStatsResponse> {
  const params = new URLSearchParams();
  params.set('start_date', query.start_date);
  params.set('end_date', query.end_date);

  if (query.username) params.set('username', query.username);
  for (const v of query.operation_types || []) params.append('operation_type', v);
  for (const v of query.model_names || []) params.append('model_name', v);
  for (const v of query.api_providers || []) params.append('api_provider', v);
  for (const v of query.statuses || []) params.append('status', v);

  if (typeof query.records_limit === 'number') params.set('records_limit', String(query.records_limit));
  if (typeof query.max_records === 'number') params.set('max_records', String(query.max_records));

  const response = await fetch(`${API_BASE_URL}/admin/usage/stats?${params.toString()}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || data.error || 'Failed to get usage stats');
  }
  return data as UsageStatsResponse;
}

export async function getUsageMetadata(username: string, generationId: string): Promise<UsageMetadataResponse> {
  const params = new URLSearchParams();
  params.set('username', username);
  params.set('generation_id', generationId);

  const response = await fetch(`${API_BASE_URL}/admin/usage/metadata?${params.toString()}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || data.error || 'Failed to get metadata');
  }
  return data as UsageMetadataResponse;
}
